import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, ThumbsUp, ThumbsDown, Bug, Lightbulb, HelpCircle, AlertTriangle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const feedbackOptions = [{
  id: "bug",
  icon: Bug,
  label: "Report Bug",
  color: "text-red-500"
}, {
  id: "feature",
  icon: Lightbulb,
  label: "Feature Request",
  color: "text-yellow-500"
}, {
  id: "help",
  icon: HelpCircle,
  label: "Need Help",
  color: "text-blue-500"
}, {
  id: "like",
  icon: ThumbsUp,
  label: "I Like This!",
  color: "text-green-500"
}, {
  id: "dislike",
  icon: ThumbsDown,
  label: "Needs Work",
  color: "text-orange-500"
}];

export const FeedbackButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!selectedType) {
      toast({
        title: "Select feedback type",
        description: "Please select what kind of feedback you want to give",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase.from("feedback").insert({
        user_id: user?.id || null,
        feedback_type: selectedType,
        message: message || null,
        email: email || null,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
      });

      if (error) throw error;

      toast({
        title: "Thanks for your feedback!",
        description: "We really appreciate you helping us improve KAIRAUX!"
      });
      setIsOpen(false);
      setSelectedType(null);
      setMessage("");
      setEmail("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Feedback sent!",
        description: "Thank you for your feedback!"
      });
      setIsOpen(false);
      setSelectedType(null);
      setMessage("");
      setEmail("");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hide feedback button on chat pages to avoid overlap
  const isChatPage = typeof window !== 'undefined' && window.location.pathname.includes('/chats');

  return <>
    <motion.button 
      onClick={() => setIsOpen(true)} 
      whileHover={{ scale: 1.05 }} 
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 2 }}
      className={`fixed right-4 z-40 items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-primary to-neon-purple text-white font-bold shadow-lg hover:shadow-xl transition-shadow flex flex-row ${
        isChatPage ? 'hidden md:flex md:bottom-6' : 'bottom-24 md:bottom-6'
      }`}
    >
      <AlertTriangle className="h-5 w-5" />
      <span className="hidden md:inline">Feedback</span>
    </motion.button>

    <AnimatePresence>
      {isOpen && <>
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="fixed inset-x-4 bottom-4 md:inset-auto md:right-4 md:bottom-4 md:w-96 z-50 glass rounded-2xl p-6 border border-primary/30"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold kairaux-text">Share Feedback</h3>
            <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-muted/50 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-muted-foreground text-sm mb-4">
            Help us improve KAIRAUX! Your feedback matters
          </p>

          {/* Quick Options */}
          <div className="grid grid-cols-5 gap-2 mb-4">
            {feedbackOptions.map(option => (
              <motion.button 
                key={option.id} 
                whileTap={{ scale: 0.9 }}
                onClick={() => setSelectedType(option.id)}
                className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                  selectedType === option.id 
                    ? "bg-primary/20 ring-2 ring-primary" 
                    : "bg-muted/30 hover:bg-muted/50"
                }`}
              >
                <option.icon className={`h-5 w-5 ${option.color}`} />
                <span className="text-[9px] mt-1 text-center leading-tight">{option.label}</span>
              </motion.button>
            ))}
          </div>

          {/* Email Input */}
          <div className="relative mb-3">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email for response"
              className="pl-10 glass border-border/50"
            />
          </div>

          {/* Message Input */}
          <Textarea 
            value={message} 
            onChange={e => setMessage(e.target.value)} 
            placeholder="Tell us more..." 
            className="min-h-[100px] mb-4 glass border-border/50" 
          />

          {/* Submit Button */}
          <Button onClick={handleSubmit} disabled={isSubmitting} className="w-full neon-glow-cyan">
            {isSubmitting ? "Sending..." : <>
              <Send className="h-4 w-4 mr-2" />
              Send Feedback
            </>}
          </Button>
        </motion.div>
      </>}
    </AnimatePresence>
  </>;
};